import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiMessageCircle } from 'react-icons/fi';
import { fetchSceneRecord } from './api';
import { sceneName } from './scenes';
import './SceneRecord.css';

// Everything the student has played, read back: the scenario, then the dated run of it, then the
// scenes that run walked, then the conversations within each in the order they were played.
//
// The grouping and the ordering are the server's — one endpoint returns the tree already shaped —
// so this screen lays out what it is given and decides nothing about it.
function SceneRecord() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSceneRecord()
      .then((record) => {
        if (!cancelled) {
          setScenarios(record.scenarios ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The date a run started is what tells two of them apart on the screen, so it is written the way
  // the reader's own language writes one.
  const dateOf = (startedAt) =>
    new Date(startedAt).toLocaleString(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });

  // Named the way a scene is named wherever the student meets it. Said once here, for the two
  // places this screen names one.
  const nameOf = (scene) => sceneName(t, scene);

  // A screen with nothing on it yet is still that screen: the message sits in the same frame the
  // record does, rather than shrinking the page to the width of a sentence.
  const message = (text) => (
    <div className="scene-record">
      <p className="scene-record-empty">{text}</p>
    </div>
  );

  const conversations = (scene) => (
    <ul>
      {scene.conversations.map((conversation) => (
        <li key={conversation.chatId}>
          <button
            type="button"
            className="scene-record-conversation"
            onClick={() => navigate(`/chats/${conversation.chatId}`)}
          >
            <FiMessageCircle aria-hidden="true" /> {conversation.name}
          </button>
        </li>
      ))}
    </ul>
  );

  if (failed) {
    return message(t('sceneRecord.failed'));
  }

  if (scenarios === null) {
    return message(t('sceneRecord.loading'));
  }

  if (scenarios.length === 0) {
    return message(t('sceneRecord.empty'));
  }

  return (
    <div className="scene-record">
      <h1 className="scene-record-title">{t('sceneRecord.title')}</h1>
      {scenarios.map((scenario) => (
        <section key={scenario.key} className="scene-record-scenario">
          {/* The key is the name everywhere else too; a scenario nobody has translated yet reads
              as its key rather than as a missing string. */}
          <h2>{t(`sceneRecord.scenarios.${scenario.key}`, scenario.key)}</h2>
          {scenario.runs.map((run) => (
            <section key={run.runId} className="scene-record-run">
              {/* A run that came in part-way along says so beside its date: two runs of one
                  scenario are the whole errand and a rehearsal of the end of it, and the date
                  alone does not say which is which. A run opened at the top came in where every
                  run of the scenario comes in, and saying so would be saying nothing. */}
              <h3>
                {dateOf(run.startedAt)}
                {run.openedAtScene &&
                  ` · ${t('sceneRecord.openedAt', { scene: nameOf(run.openedAtScene) })}`}
              </h3>
              {run.scenes.map((scene) => (
                <section key={scene.sceneRunId} className="scene-record-scene">
                  <h4>{nameOf(scene.key)}</h4>
                  {conversations(scene)}
                </section>
              ))}
            </section>
          ))}
        </section>
      ))}
    </div>
  );
}

export default SceneRecord;
